// src/components/Creators/Creators.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import API from '../config';
import UpgradeModal from '../Subscription/UpgradeModal';
import BlurOverlay from '../Subscription/BlurOverlay';
import {
  Box,
  Button,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Stack,
  Chip,
  Rating,
  Paper,
  InputAdornment,
  Divider,
  Fade,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Collapse,
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  Search as SearchIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const recommendationOptions = [
  { value: null, label: 'All Recommendations' },
  { value: 50, label: '50%+' },
  { value: 75, label: '75%+' },
  { value: 90, label: '90%+' },
];

const spendOptions = [
  { value: '', label: 'All Spend Ranges' },
  { value: 'Under $10', label: 'Under $10' },
  { value: '$10-$25', label: '$10–25' },
  { value: '$25-$50', label: '$25–50' },
  { value: '$50+', label: '$50+' },
];

const sortOptions = [
  { value: 'recDesc', label: 'Most Recommended' },
  { value: 'reviewsDesc', label: 'Most Reviewed' },
  { value: 'spendAsc', label: 'Lowest Average Spend' },
];

export default function Creators() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const theme = useTheme();

  // State
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [recFilter, setRecFilter] = useState(null);
  const [spendFilter, setSpendFilter] = useState('');
  const [sortOption, setSortOption] = useState('recDesc');
  const [activeFilters, setActiveFilters] = useState(0);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCreators, setTotalCreators] = useState(0);

  // Handle responsive filter display
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= theme.breakpoints.values.md) {
        setShowMobileFilters(true); // Always show on desktop
      } else {
        setShowMobileFilters(false); // Hide by default on mobile
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [theme.breakpoints.values.md]);

  // Fetch creators with pagination and filters
  const fetchCreators = async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      }
      
      const params = {
        page,
        limit: hasActiveSubscription ? 50 : 6,
      };
      
      // Add filters if user is VIP
      if (hasActiveSubscription) {
        if (searchTerm) params.search = searchTerm;
        if (recFilter !== null) params.recFilter = recFilter;
        if (spendFilter) params.spendFilter = spendFilter;
        if (sortOption) params.sort = sortOption;
      }
      
      const creatorsResponse = await axios.get(`${API}/api/creators`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params,
      });
      
      const newCreators = creatorsResponse.data.creators || [];
      const pagination = creatorsResponse.data.pagination;
      
      if (append) {
        setCreators(prev => [...prev, ...newCreators]);
      } else {
        setCreators(newCreators);
      }
      
      setHasMore(pagination?.hasMore || false);
      setTotalCreators(pagination?.total || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error(err);
      setError('Failed to load creators.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        let isVip = false;
        // Fetch subscription status first if user is logged in
        if (token) {
          const dashboardResponse = await axios.get(`${API}/api/dashboard`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const subscriptionStatus = dashboardResponse.data.profile?.subscriptionStatus;
          isVip = ['active', 'cancel_at_period_end', 'trialing', 'past_due', 'promo_active'].includes(subscriptionStatus);
          setHasActiveSubscription(isVip);
        }
        
        // Then fetch first page of creators with correct limit
        const params = {
          page: 1,
          limit: isVip ? 50 : 6,
        };
        
        const creatorsResponse = await axios.get(`${API}/api/creators`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params,
        });
        
        const newCreators = creatorsResponse.data.creators || [];
        const pagination = creatorsResponse.data.pagination;
        
        setCreators(newCreators);
        setHasMore(pagination?.hasMore || false);
        setTotalCreators(pagination?.total || 0);
        setCurrentPage(1);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Failed to load data.');
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);
  
  // Refetch when filters change (reset to page 1)
  useEffect(() => {
    if (hasActiveSubscription && !loading) {
      setCreators([]);
      setCurrentPage(1);
      fetchCreators(1, false);
    }
  }, [searchTerm, recFilter, spendFilter, sortOption]);
  
  // Load more function
  const loadMore = () => {
    if (!loadingMore && hasMore && hasActiveSubscription) {
      fetchCreators(currentPage + 1, true);
    }
  };

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (searchTerm) count++;
    if (recFilter !== null) count++;
    if (spendFilter) count++;
    setActiveFilters(count);
  }, [searchTerm, recFilter, spendFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setRecFilter(null);
    setSpendFilter('');
  };

  // Creators are already filtered and sorted by backend
  // Just use them directly
  const filtered = creators;

  // Loading states
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
          Loading creators...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Discover Top Creators
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Browse and filter through our curated list of creators, rated by real subscribers.
        </Typography>
      </Box>

      {/* Filters */}
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
              theme.palette.background.paper,
              0.98
            )})`,
            backdropFilter: 'blur(20px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          {/* Mobile Filter Toggle */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              startIcon={<FilterIcon />}
              endIcon={showMobileFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                py: 1.5,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
              {activeFilters > 0 && (
                <Chip
                  label={activeFilters}
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.75rem',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                  }}
                />
              )}
            </Button>
            {!hasActiveSubscription && (
              <Typography variant="body2" sx={{ 
                mt: 1, 
                textAlign: 'center',
                color: theme.palette.primary.main, 
                fontWeight: 500,
                fontSize: '0.875rem'
              }}>
                🔒 Upgrade to VIP to access all {totalCreators}+ creators and enable advanced filtering
              </Typography>
            )}
          </Box>

          {/* Desktop Header */}
          <Box sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
              Filter & Search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hasActiveSubscription 
                ? 'Find the perfect creators for you' 
                : 'Search and filter through your preview of creators'
              }
            </Typography>
            {!hasActiveSubscription && (
              <Typography variant="body2" sx={{ 
                mt: 1, 
                color: theme.palette.primary.main, 
                fontWeight: 500,
                fontSize: '0.875rem'
              }}>
                🔒 Upgrade to VIP to access all {totalCreators}+ creators and enable advanced filtering
              </Typography>
            )}
          </Box>

          {/* Filter Content */}
          <Collapse in={showMobileFilters}>
            <Grid container spacing={3}>
              {/* Search */}
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: theme.palette.text.secondary }}>
                    Search Creators
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={hasActiveSubscription ? "Type creator name..." : "Search disabled - upgrade to VIP"}
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={!hasActiveSubscription}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: hasActiveSubscription 
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha(theme.palette.grey[300], 0.3),
                        '&:hover': {
                          backgroundColor: hasActiveSubscription 
                            ? alpha(theme.palette.background.paper, 0.9)
                            : alpha(theme.palette.grey[300], 0.3),
                        },
                        '&.Mui-focused': {
                          backgroundColor: hasActiveSubscription 
                            ? theme.palette.background.paper
                            : alpha(theme.palette.grey[300], 0.3),
                          boxShadow: hasActiveSubscription 
                            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                            : 'none',
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton 
                            size="small" 
                            onClick={() => setSearchTerm('')}
                            sx={{ 
                              color: theme.palette.text.secondary,
                              '&:hover': { color: theme.palette.primary.main }
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>

              {/* Recommendation Filter */}
              <Grid item xs={12} sm={6} md={2.5}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: theme.palette.text.secondary }}>
                    Recommendation
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={recFilter ?? ''}
                      onChange={(e) => setRecFilter(e.target.value ? Number(e.target.value) : null)}
                      displayEmpty
                      disabled={!hasActiveSubscription}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: hasActiveSubscription 
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha(theme.palette.grey[300], 0.3),
                        '&:hover': {
                          backgroundColor: hasActiveSubscription 
                            ? alpha(theme.palette.background.paper, 0.9)
                            : alpha(theme.palette.grey[300], 0.3),
                        },
                        '&.Mui-focused': {
                          backgroundColor: hasActiveSubscription 
                            ? theme.palette.background.paper
                            : alpha(theme.palette.grey[300], 0.3),
                          boxShadow: hasActiveSubscription 
                            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                            : 'none',
                        },
                      }}
                      renderValue={(value) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ThumbUp sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                          <Typography variant="body2">
                            {value ? `${value}%+` : 'All Levels'}
                          </Typography>
                        </Box>
                      )}
                    >
                      {recommendationOptions.map((o) => (
                        <MenuItem key={o.value ?? 'all'} value={o.value ?? ''}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ThumbUp sx={{ fontSize: '0.9rem', color: theme.palette.primary.main }} />
                            {o.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Spend Filter */}
              <Grid item xs={12} sm={6} md={2.5}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: theme.palette.text.secondary }}>
                    Spend Range
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={spendFilter}
                      onChange={(e) => setSpendFilter(e.target.value)}
                      displayEmpty
                      disabled={!hasActiveSubscription}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: hasActiveSubscription 
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha(theme.palette.grey[300], 0.3),
                        '&:hover': {
                          backgroundColor: hasActiveSubscription 
                            ? alpha(theme.palette.background.paper, 0.9)
                            : alpha(theme.palette.grey[300], 0.3),
                        },
                        '&.Mui-focused': {
                          backgroundColor: hasActiveSubscription 
                            ? theme.palette.background.paper
                            : alpha(theme.palette.grey[300], 0.3),
                          boxShadow: hasActiveSubscription 
                            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                            : 'none',
                        },
                      }}
                      renderValue={(value) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MoneyIcon sx={{ fontSize: '1rem', color: theme.palette.success.main }} />
                          <Typography variant="body2">
                            {value || 'All Ranges'}
                          </Typography>
                        </Box>
                      )}
                    >
                      {spendOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MoneyIcon sx={{ fontSize: '0.9rem', color: theme.palette.success.main }} />
                            {o.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Sort Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: theme.palette.text.secondary }}>
                    Sort By
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                      disabled={!hasActiveSubscription}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: hasActiveSubscription 
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha(theme.palette.grey[300], 0.3),
                        '&:hover': {
                          backgroundColor: hasActiveSubscription 
                            ? alpha(theme.palette.background.paper, 0.9)
                            : alpha(theme.palette.grey[300], 0.3),
                        },
                        '&.Mui-focused': {
                          backgroundColor: hasActiveSubscription 
                            ? theme.palette.background.paper
                            : alpha(theme.palette.grey[300], 0.3),
                          boxShadow: hasActiveSubscription 
                            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                            : 'none',
                        },
                      }}
                      renderValue={(value) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SortIcon sx={{ fontSize: '1rem', color: theme.palette.secondary.main }} />
                          <Typography variant="body2">
                            {sortOptions.find(opt => opt.value === value)?.label || 'Most Recommended'}
                          </Typography>
                        </Box>
                      )}
                    >
                      {sortOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SortIcon sx={{ fontSize: '0.9rem', color: theme.palette.secondary.main }} />
                            {o.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Active filters & clear button */}
              {activeFilters > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FilterIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {activeFilters} active filter{activeFilters !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Collapse>
        </Paper>
      </Container>

      {/* Results count */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" fontWeight={500}>
          {hasActiveSubscription ? (
            `Showing ${filtered.length} of ${totalCreators} Creator${totalCreators !== 1 ? 's' : ''}`
          ) : (
            `Showing ${Math.min(6, filtered.length)} of ${filtered.length} Creator${filtered.length !== 1 ? 's' : ''}`
          )}
        </Typography>
      </Box>

      {/* Creator cards */}
      <Grid container spacing={4} justifyContent="center">
        {filtered.length === 0 ? (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.7),
              }}
            >
              <Typography variant="h6" gutterBottom>
                No creators match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Try adjusting your search criteria or clearing some filters.
              </Typography>
              <Chip
                label="Clear All Filters"
                onClick={clearFilters}
                color="primary"
                sx={{ mt: 1 }}
              />
            </Paper>
          </Grid>
        ) : (
          // Show first 6 creators normally
          filtered.slice(0, hasActiveSubscription ? filtered.length : 6).map((creator, index) => (
            <Fade key={creator.id} in={true} timeout={300 + index * 100}>
              <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  sx={{
                    height: 420, // Fixed height for consistency
                    width: 320, // Fixed width for exact consistency
                    maxWidth: 320, // Prevent any overflow
                    minWidth: 320, // Ensure minimum width
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  {/* Banner Image */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: 160,
                      width: '100%',
                      overflow: 'hidden',
                      backgroundImage: `url(${creator.bannerImage || '/default-banner.jpg'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '60%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                      },
                    }}
                  />

                  {/* Profile Picture */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      mt: -6,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        border: `4px solid ${theme.palette.background.paper}`,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                        overflow: 'hidden',
                        bgcolor: theme.palette.background.paper,
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={creator.profilePicture || '/default-profile.png'}
                        alt={creator.username}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Content */}
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3, pt: 1 }}>
                    {/* Username and Recommendation Badge */}
                    <Box sx={{ textAlign: 'center', mb: creator.reviewCount === 0 ? 1.5 : 3 }}>
                      <Typography 
                        variant="h6" 
                        component="h2" 
                        sx={{ 
                          fontWeight: 700,
                          mb: 1.5,
                          color: theme.palette.text.primary,
                          fontSize: '1.2rem',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {creator.username}
                      </Typography>
                      
                      {creator.reviewCount === 0 ? (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 2,
                            py: 0.5,
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            No Reviews Yet
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 2,
                            py: 0.5,
                            borderRadius: 3,
                            bgcolor: 
                              creator.recPct >= 75
                                ? alpha(theme.palette.success.main, 0.1)
                                : creator.recPct >= 50
                                ? alpha(theme.palette.primary.main, 0.1)
                                : alpha(theme.palette.error.main, 0.1),
                            border: `1px solid ${
                              creator.recPct >= 75
                                ? alpha(theme.palette.success.main, 0.3)
                                : creator.recPct >= 50
                                ? alpha(theme.palette.primary.main, 0.3)
                                : alpha(theme.palette.error.main, 0.3)
                            }`,
                          }}
                        >
                          <Box
                            sx={{
                              color: 
                                creator.recPct >= 75
                                  ? theme.palette.success.main
                                  : creator.recPct >= 50
                                  ? theme.palette.primary.main
                                  : theme.palette.error.main,
                            }}
                          >
                            {creator.recPct >= 50 ? (
                              <ThumbUp sx={{ fontSize: '1rem' }} />
                            ) : (
                              <ThumbDown sx={{ fontSize: '1rem' }} />
                            )}
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              color: 
                                creator.recPct >= 75
                                  ? theme.palette.success.main
                                  : creator.recPct >= 50
                                  ? theme.palette.primary.main
                                  : theme.palette.error.main,
                            }}
                          >
                            {Math.round(creator.recPct)}% Recommend
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Stats or Call to Action */}
                    {creator.reviewCount === 0 ? (
                      <Box sx={{ mb: 2, flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                        <Box sx={{ textAlign: 'center', maxWidth: 280 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontSize: '1.15rem',
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              mb: 1,
                            }}
                          >
                            Be the First to Review! ✨
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}
                          >
                            Help the community by sharing your experience!
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Stack spacing={1.5} sx={{ mb: 3, flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              Rating
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating
                              value={creator.averageRating}
                              precision={0.5}
                              size="small"
                              readOnly
                              sx={{ 
                                '& .MuiRating-icon': { fontSize: '0.9rem' },
                                '& .MuiRating-iconFilled': { color: theme.palette.primary.main }
                              }}
                            />
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem', color: theme.palette.text.primary }}>
                            {creator.averageRating.toFixed(1)}
                          </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '1rem' }}>💬</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              Reviews
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem', color: theme.palette.text.primary }}>
                            {creator.reviewCount}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MoneyIcon sx={{ fontSize: '1rem', color: theme.palette.success.main }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              Avg Spend
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem', color: theme.palette.text.primary }}>
                            {creator.avgSpent > 0 ? `$${creator.avgSpent.toFixed(0)}` : 'N/A'}
                          </Typography>
                        </Box>
                      </Stack>
                    )}

                    {/* Action Button */}
                    <Button
                      component={RouterLink}
                      to={`/creator/${creator.id}`}
                      variant="contained"
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        bgcolor: theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      View Profile
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Fade>
          ))
        )}

      </Grid>

      {/* Load More Button for VIP users */}
      {hasActiveSubscription && hasMore && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={loadMore}
            disabled={loadingMore}
            sx={{
              minWidth: 200,
              py: 1.5,
              px: 4,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {loadingMore ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Loading More...
              </>
            ) : (
              `Load More Creators (${creators.length} of ${totalCreators})`
            )}
          </Button>
        </Box>
      )}

      {/* Blur Overlay for Non-Subscribers - full width outside grid */}
      {!hasActiveSubscription && totalCreators > 6 && (
        <Box sx={{ mt: 4, width: '100%' }}>
          <BlurOverlay
            onSubscribe={() => setShowUpgradeModal(true)}
            lockedCount={totalCreators - 6}
            totalCount={totalCreators}
            message={`Showing 6 out of ${totalCreators} creators`}
          />
        </Box>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </Container>
  );
}
